import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import StickyNote2RoundedIcon from '@mui/icons-material/StickyNote2Rounded';
import PushPinRoundedIcon from '@mui/icons-material/PushPinRounded';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import ArchiveRoundedIcon from '@mui/icons-material/ArchiveRounded';

import { applicationsApi, type ApplicationNoteDto } from '../../api/admin';
import { editErrorMessage } from '../../lib/applicationFieldEditing';
import { ApplicationSectionCard } from './applicationSectionChrome';

const MAX_BODY = 4000;

function NoteRow({
  note,
  onEdit,
  onTogglePin,
  onArchive,
  busy,
}: {
  note: ApplicationNoteDto;
  onEdit: () => void;
  onTogglePin: () => void;
  onArchive: () => void;
  busy: boolean;
}) {
  const author = note.authorEmail?.trim() || note.authorUserId;
  const when = new Date(note.createdAt);
  const edited = note.updatedAt !== note.createdAt;

  return (
    <Box sx={{ py: 0.5, opacity: note.archivedAt ? 0.55 : 1 }}>
      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
        {note.pinned ? (
          <PushPinRoundedIcon sx={{ fontSize: 14, color: 'warning.main' }} />
        ) : null}
        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          {author}
        </Typography>
        <Typography variant="caption" color="text.disabled">
          {Number.isFinite(when.getTime()) ? when.toLocaleString() : note.createdAt}
        </Typography>
        {note.category ? (
          <Chip size="small" variant="outlined" label={note.category.replace(/_/g, ' ').toLowerCase()} sx={{ height: 18, fontSize: '0.65rem' }} />
        ) : null}
        {edited ? (
          <Tooltip title={`Edited ${new Date(note.updatedAt).toLocaleString()}`} arrow>
            <Typography variant="caption" color="text.disabled">
              (edited)
            </Typography>
          </Tooltip>
        ) : null}
        {note.archivedAt ? (
          <Chip size="small" label="archived" sx={{ height: 18, fontSize: '0.65rem' }} />
        ) : null}

        {!note.archivedAt ? (
          <Stack direction="row" spacing={0.25} sx={{ ml: 'auto' }}>
            <Tooltip title={note.pinned ? 'Unpin' : 'Pin to the top'} arrow>
              <IconButton size="small" onClick={onTogglePin} disabled={busy || !note.editableByCaller}>
                {note.pinned ? (
                  <PushPinRoundedIcon sx={{ fontSize: 15 }} />
                ) : (
                  <PushPinOutlinedIcon sx={{ fontSize: 15 }} />
                )}
              </IconButton>
            </Tooltip>
            {/* Only the author may edit. The server decides; this mirrors it. */}
            {note.editableByCaller ? (
              <Tooltip title="Edit your note" arrow>
                <IconButton size="small" onClick={onEdit} disabled={busy}>
                  <EditRoundedIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            ) : null}
            {/* Author or super admin. Shown only when the server says so — a
                button that 403s on a colleague's note is worse than no button. */}
            {note.archivableByCaller ? (
              <Tooltip title="Archive — nothing is deleted" arrow>
                <IconButton size="small" onClick={onArchive} disabled={busy}>
                  <ArchiveRoundedIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            ) : null}
          </Stack>
        ) : null}
      </Stack>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.35, lineHeight: 1.55 }}>
        {note.body}
      </Typography>
    </Box>
  );
}

/**
 * The note thread.
 *
 * Deliberately available on every application, including terminal ones — a funded
 * loan that turns out to involve a fraudulent employer, or a decline a phone call
 * showed to be wrong, are exactly the cases worth writing down.
 *
 * The composer warns that a note is permanent and disclosable. Reviewers paste
 * customer statements into these, and they surface in DSAR requests and legal
 * discovery; someone treating the box like a sticky note is a real risk.
 */
export function ApplicationNotesPanel({ applicationId }: { applicationId: string }) {
  const queryClient = useQueryClient();
  const [body, setBody] = useState('');
  const [category, setCategory] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingBody, setEditingBody] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const notesQ = useQuery({
    queryKey: ['applicationNotes', applicationId, showArchived],
    queryFn: () => applicationsApi.notes(applicationId, { includeArchived: showArchived }),
    enabled: !!applicationId,
    retry: false,
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['applicationNotes', applicationId] });
    // A note mutation is itself audited, so the change history is stale too.
    void queryClient.invalidateQueries({ queryKey: ['applicationChanges', applicationId] });
  };

  const createM = useMutation({
    mutationFn: () =>
      applicationsApi.createNote(applicationId, {
        body: body.trim(),
        category: category || null,
      }),
    onSuccess: () => {
      setBody('');
      setCategory('');
      setError(null);
      refresh();
    },
    onError: (e) => setError(editErrorMessage(e)),
  });

  const updateM = useMutation({
    mutationFn: (input: { noteId: string; body?: string; pinned?: boolean }) =>
      applicationsApi.updateNote(applicationId, input.noteId, {
        body: input.body,
        pinned: input.pinned,
      }),
    onSuccess: () => {
      setEditingId(null);
      setError(null);
      refresh();
    },
    onError: (e) => setError(editErrorMessage(e)),
  });

  const archiveM = useMutation({
    mutationFn: (noteId: string) => applicationsApi.archiveNote(applicationId, noteId),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (e) => setError(editErrorMessage(e)),
  });

  const busy = createM.isPending || updateM.isPending || archiveM.isPending;
  const notes = notesQ.data?.items ?? [];
  const categories = notesQ.data?.categories ?? [];

  return (
    <ApplicationSectionCard
      title="Notes"
      subtitle="Context that does not belong to any one field"
      icon={<StickyNote2RoundedIcon fontSize="small" />}
      action={
        <Button size="small" color="inherit" onClick={() => setShowArchived((v) => !v)}>
          {showArchived ? 'Hide archived' : 'Show archived'}
        </Button>
      }
    >
      <Stack spacing={2}>
        {error ? (
          <Alert severity="error" variant="outlined" onClose={() => setError(null)}>
            {error}
          </Alert>
        ) : null}

        {/* Composer */}
        <Stack spacing={1}>
          <TextField
            value={body}
            onChange={(e) => setBody(e.target.value.slice(0, MAX_BODY))}
            placeholder="Customer called — switching employers next month. Third IBV attempt timed out on RBC."
            multiline
            minRows={2}
            size="small"
            fullWidth
            disabled={busy}
          />
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <TextField
              select
              size="small"
              label="Category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              disabled={busy}
              sx={{ minWidth: 190 }}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {categories.map((c) => (
                <MenuItem key={c.code} value={c.code}>
                  {c.label}
                </MenuItem>
              ))}
            </TextField>
            <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 200 }}>
              Permanent and attributed to you. Notes appear in data-subject access requests and
              legal discovery.
            </Typography>
            <Button
              variant="contained"
              size="small"
              disabled={busy || body.trim().length === 0}
              onClick={() => createM.mutate()}
              startIcon={createM.isPending ? <CircularProgress size={13} color="inherit" /> : undefined}
            >
              Add note
            </Button>
          </Stack>
        </Stack>

        <Divider />

        {notesQ.isPending ? (
          <Box sx={{ py: 2, textAlign: 'center' }}>
            <CircularProgress size={20} />
          </Box>
        ) : notesQ.isError ? (
          <Alert severity="error" variant="outlined">
            Could not load notes for this application.
          </Alert>
        ) : notes.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No notes yet. Anything you learn outside the automated checks belongs here.
          </Typography>
        ) : (
          <Stack spacing={1.25} divider={<Divider flexItem />}>
            {notes.map((note) =>
              editingId === note.id ? (
                <Stack key={note.id} spacing={1}>
                  <TextField
                    value={editingBody}
                    onChange={(e) => setEditingBody(e.target.value.slice(0, MAX_BODY))}
                    multiline
                    minRows={2}
                    size="small"
                    fullWidth
                    disabled={busy}
                    autoFocus
                  />
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button size="small" color="inherit" onClick={() => setEditingId(null)} disabled={busy}>
                      Cancel
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      disabled={busy || editingBody.trim().length === 0}
                      onClick={() => updateM.mutate({ noteId: note.id, body: editingBody.trim() })}
                    >
                      Save
                    </Button>
                  </Stack>
                </Stack>
              ) : (
                <NoteRow
                  key={note.id}
                  note={note}
                  busy={busy}
                  onEdit={() => {
                    setEditingId(note.id);
                    setEditingBody(note.body);
                  }}
                  onTogglePin={() =>
                    updateM.mutate({ noteId: note.id, pinned: !note.pinned })
                  }
                  onArchive={() => archiveM.mutate(note.id)}
                />
              ),
            )}
          </Stack>
        )}
      </Stack>
    </ApplicationSectionCard>
  );
}
